import {
  Gender,
  getDialectLabel,
  LANGUAGE_CONFIG,
  LANGUAGE_OPTIONS,
} from '../../data/models/AI/VideoGenerationOptions';
import {getLocalizationBasedOnLang} from '../../data/models/User/UserInfo';

/**
 * A narration language and its dialects. Each dialect names its default voice,
 * Emma (female) or James (male), or null where only library voices speak it.
 */
interface StudioLanguage {
  label: string;
  dialects: Record<string, Gender | null>;
}

/**
 * The rest of the languages Cartesia's library voices narrate, offered with
 * the voice library and sent as the same codes as the New microreel page's
 * ('uk' + 'UA'). Emma and James speak Russian; the others need a library voice.
 * Keep in step with StudioOnlyLanguages in TapyblEduApi's LanguageRegressionTests.
 */
export const STUDIO_ONLY_LANGUAGES: Record<string, StudioLanguage> = {
  hr: {label: 'Croatian', dialects: {HR: null}},
  fi: {label: 'Finnish', dialects: {FI: null}},
  ka: {label: 'Georgian', dialects: {GE: null}},
  gu: {label: 'Gujarati', dialects: {IN: null}},
  id: {label: 'Indonesian', dialects: {ID: null}},
  ja: {label: 'Japanese', dialects: {JP: null}},
  kn: {label: 'Kannada', dialects: {IN: null}},
  ko: {label: 'Korean', dialects: {KR: null}},
  ms: {label: 'Malay', dialects: {MY: null}},
  ml: {label: 'Malayalam', dialects: {IN: null}},
  mr: {label: 'Marathi', dialects: {IN: null}},
  no: {label: 'Norwegian', dialects: {NO: null}},
  or: {label: 'Odia', dialects: {IN: null}},
  pa: {label: 'Punjabi', dialects: {IN: null}},
  ro: {label: 'Romanian', dialects: {RO: null}},
  ru: {label: 'Russian', dialects: {RU: 'female'}},
  sk: {label: 'Slovak', dialects: {SK: null}},
  tl: {label: 'Tagalog', dialects: {PH: null}},
  ta: {label: 'Tamil', dialects: {IN: null}},
  te: {label: 'Telugu', dialects: {IN: null}},
  th: {label: 'Thai', dialects: {TH: null}},
  uk: {label: 'Ukrainian', dialects: {UA: null}},
  ur: {label: 'Urdu', dialects: {PK: null}},
  vi: {label: 'Vietnamese', dialects: {VN: null}},
};

const LANGUAGES: Record<string, StudioLanguage> = {
  ...LANGUAGE_CONFIG,
  ...STUDIO_ONLY_LANGUAGES,
};

/**
 * The language the page is read in. English names come from the New microreel page's lists;
 * a Welsh page names languages and countries in Welsh, as the browser knows them.
 */
const DISPLAY_LOCALE: 'en' | 'cy' = getLocalizationBasedOnLang('en', 'cy');

const displayNames = (type: 'language' | 'region') => {
  try {
    return new Intl.DisplayNames([DISPLAY_LOCALE], {type});
  } catch {
    return null;
  }
};
const languageNames = DISPLAY_LOCALE === 'en' ? null : displayNames('language');
const regionNames = displayNames('region');

/** A code's name in the page's language, or `fallback` where the browser has none. */
const nameOf = (
  names: Intl.DisplayNames | null,
  code: string,
  fallback: string,
) => {
  try {
    const name = names?.of(code);
    return name && name !== code ? name : fallback;
  } catch {
    return fallback;
  }
};

export const languageLabel = (language: string) => {
  const label = LANGUAGES[language]?.label ?? language;
  return languageNames ? nameOf(languageNames, language, label) : label;
};

const sortByName = (options: {key: string; value: string}[]) =>
  [...options].sort((a, b) => a.value.localeCompare(b.value));

/** The New microreel page's languages, and with the voice library every other language its voices speak. */
export const languageOptions = (withLibrary: boolean) =>
  withLibrary
    ? sortByName(
        Object.keys(LANGUAGES).map(key => ({key, value: languageLabel(key)})),
      )
    : languageNames
    ? sortByName(
        LANGUAGE_OPTIONS.map(({key}) => ({key, value: languageLabel(key)})),
      )
    : LANGUAGE_OPTIONS;

// The New microreel page sends the United Kingdom as 'UK', which is not a country code.
const COUNTRY_CODES: Record<string, string> = {UK: 'GB'};

// A code that is not a country (say a future 'LATAM') shows as itself rather than breaking the page.
const regionName = (dialect: string) =>
  nameOf(regionNames, COUNTRY_CODES[dialect] ?? dialect, dialect);

/** The dialect's country; the code sent stays the same. Chinese is sent as 'CH', China, not Switzerland. */
export const dialectLabel = (language: string, dialect: string) =>
  language === 'zh' && dialect === 'CH'
    ? nameOf(regionNames, 'CN', 'China')
    : language === 'en' && DISPLAY_LOCALE === 'en'
    ? getDialectLabel(language, dialect)
    : regionName(dialect);

export const dialectOptions = (language: string) =>
  Object.keys(LANGUAGES[language]?.dialects ?? {}).map(key => ({
    key,
    value: dialectLabel(language, key),
  }));

/** English starts on US; other languages on their first dialect. */
export const defaultDialect = (language: string) => {
  const dialects = LANGUAGES[language]?.dialects ?? {};
  return language === 'en' && 'US' in dialects
    ? 'US'
    : Object.keys(dialects)[0];
};

/** The dialect's default voice, or null where Emma and James don't speak the language. */
export const defaultVoice = (language: string, dialect: string) => {
  const gender = LANGUAGES[language]?.dialects[dialect];
  return gender ? (gender === 'female' ? 'Emma' : 'James') : null;
};

/** Emma and James don't speak the language, so a library voice narrates it. */
export const libraryOnly = (language: string) =>
  !Object.values(LANGUAGES[language]?.dialects ?? {}).some(Boolean);
